/**
 * PermissionManager - Centralized permission management system
 */

import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import {
  PERMISSION_ACTION_ANY,
  resolvePermissionActions,
  roleHasPermission,
  type PermissionMap,
} from './permissionResolver';
import { hasPermissionInCompany } from './moduleCompanyPermissions';

export interface Permission {
  resource: string;
  action: string;
}

export interface Role {
  role_name: string;
  role_level: string;
  permissions?: PermissionMap;
}

export interface CompanyPermission {
  company_id: string;
  company_name: string;
  company_code: string;
  company_type: string;
  roles: Role[];
}

export interface User {
  id: string | number;
  email: string;
  username: string;
  role?: string;
  role_display_name?: string;
}

export interface PermissionContext {
  user: User;
  globalRoles: Role[];
  selectedCompany: CompanyPermission | null;
  hasGlobalRole?: boolean;
}

const isGlobalRoleRecord = (role: Partial<Role>): boolean =>
  role?.role_name === 'global' ||
  role?.role_level === 'global' ||
  role?.role_name === 'global_admin';

export const hasGlobalRoleScope = (
  globalRoles: Array<Partial<Role>> = [],
  selectedCompany?: { company_id?: string; roles?: Array<Partial<Role>> } | null,
  hasGlobalRoleFlag?: boolean
): boolean => {
  if (hasGlobalRoleFlag === true) {
    return true;
  }

  if (selectedCompany?.company_id === 'global') {
    return true;
  }

  if (Array.isArray(globalRoles) && globalRoles.some(isGlobalRoleRecord)) {
    return true;
  }

  const companyRoles: Array<Partial<Role>> = Array.isArray(selectedCompany?.roles)
    ? selectedCompany?.roles ?? []
    : [];

  return companyRoles.some(isGlobalRoleRecord);
};

export class PermissionManager {
  private context: PermissionContext;

  constructor(context: PermissionContext) {
    this.context = context;
  }

  updateContext(context: PermissionContext): void {
    this.context = context;
  }

  hasPermission(resource: string, action: string): boolean {
    if (this.hasGlobalRole()) {
      return true;
    }

    if (this.hasGlobalRoleInCompany()) {
      return true;
    }

    return this.hasSpecificPermission(resource, action);
  }

  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some((permission) =>
      this.hasPermission(permission.resource, permission.action)
    );
  }

  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every((permission) =>
      this.hasPermission(permission.resource, permission.action)
    );
  }

  hasRole(roleName: string): boolean {
    if (this.context.globalRoles.some(
      (role) =>
        role.role_name === roleName ||
        role.role_level === roleName ||
        role.role_name === `${roleName}_admin`
    )) {
      return true;
    }

    if (this.context.selectedCompany?.roles) {
      return this.context.selectedCompany.roles.some(
        (role) =>
          role.role_name === roleName ||
          role.role_level === roleName ||
          role.role_name === `${roleName}_admin`
      );
    }

    return false;
  }

  getResourcePermissions(resource: string): string[] {
    if (this.hasGlobalRole() || this.hasGlobalRoleInCompany()) {
      return ['read', 'create', 'update', 'delete'];
    }

    if (!this.context.selectedCompany?.roles) {
      return [];
    }

    const permissions: string[] = [];
    for (const role of this.context.selectedCompany.roles) {
      const actions = resolvePermissionActions(role.permissions, resource);
      if (actions) {
        permissions.push(...actions);
      }
    }

    return Array.from(new Set(permissions));
  }

  canRead(resource: string): boolean {
    return this.hasPermission(resource, 'read');
  }

  canCreate(resource: string): boolean {
    return this.hasPermission(resource, 'create');
  }

  canUpdate(resource: string): boolean {
    return this.hasPermission(resource, 'update');
  }

  canDelete(resource: string): boolean {
    return this.hasPermission(resource, 'delete');
  }

  /**
   * Toolbar/entity company scoped permission check using `auth.companies` payload.
   * Prefer this over login-scoped `hasPermission` when evaluating a specific company id.
   */
  hasPermissionInCompany(
    companies: CompanyData[],
    companyId: string | null | undefined,
    resource: string,
    action: string
  ): boolean {
    return hasPermissionInCompany({
      companies,
      companyId,
      resource,
      action,
      globalRoles: this.context.globalRoles,
      selectedCompany: this.context.selectedCompany,
      hasGlobalRole: this.context.hasGlobalRole,
    });
  }

  getPermissionSummary(resource: string): {
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
  } {
    return {
      canRead: this.canRead(resource),
      canCreate: this.canCreate(resource),
      canUpdate: this.canUpdate(resource),
      canDelete: this.canDelete(resource),
    };
  }

  canAccessModule(
    _moduleName: string,
    requiredPermissions: Array<{ resource: string; action: string }>
  ): boolean {
    if (this.hasGlobalRole()) {
      return true;
    }

    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    return requiredPermissions.some(({ resource, action }) =>
      this.hasPermission(resource, action)
    );
  }

  private hasGlobalRole(): boolean {
    return hasGlobalRoleScope(
      this.context.globalRoles,
      this.context.selectedCompany,
      this.context.hasGlobalRole
    );
  }

  private hasGlobalRoleInCompany(): boolean {
    if (!this.context.selectedCompany?.roles) {
      return false;
    }

    return this.context.selectedCompany.roles.some(isGlobalRoleRecord);
  }

  hasAnyPermissionOnResource(resource: string): boolean {
    if (this.hasGlobalRole() || this.hasGlobalRoleInCompany()) {
      return true;
    }

    if (!this.context.selectedCompany?.roles) {
      return false;
    }

    return this.context.selectedCompany.roles.some((role) =>
      roleHasPermission(role, resource, PERMISSION_ACTION_ANY)
    );
  }

  private hasSpecificPermission(resource: string, action: string): boolean {
    if (!this.context.selectedCompany?.roles) {
      return false;
    }

    for (const role of this.context.selectedCompany.roles) {
      if (roleHasPermission(role, resource, action)) {
        return true;
      }
    }

    return false;
  }
}

export const createPermissionManager = (context: PermissionContext): PermissionManager => {
  return new PermissionManager(context);
};

export const PERMISSION_RESOURCES = {
  USERS: 'users',
  ROLES: 'roles',
  COMPANIES: 'companies',
  DEPARTMENTS: 'departments',
  EMPLOYEES: 'employees',
  EMPLOYEES_FIXED_SALARY: 'employees.fixedsalary',
  EMPLOYEES_INSURANCE: 'employees.insurance',
  EMPLOYEES_INSURANCE_PAYMENT: 'employees.insurancepayment',
  CUSTOMERS: 'customer',
  SUPPLIERS: 'supplier',
  ORDERS: 'orders',
  TRANSACTIONS: 'transaction',
  TRANSACTION: 'transaction',
  TRANSACTION_TYPE: 'transactiontype',
  WALLET: 'wallet',
  DOCUMENTS: 'documents',
  GROUPS: 'groups',
  LEAD: 'lead',
  CONTACTS: 'contacts',
  VIEW_OTHERS_CUSTOMER: 'view_others_customer',
  VIEW_OTHERS_ORDER: 'view_others_order',
  REPORTS: 'reports',
  INVENTORY_ITEM: 'inventory.item',
  INVENTORY_MATERIAL_REQUEST: 'inventory.materialrequest',
} as const;

export const PERMISSION_ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

export type PermissionResource = typeof PERMISSION_RESOURCES[keyof typeof PERMISSION_RESOURCES];
export type PermissionAction = typeof PERMISSION_ACTIONS[keyof typeof PERMISSION_ACTIONS];
